import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { AlertTriangle, CalendarClock, Clock } from 'lucide-react'
import { Link } from 'react-router'
import apiClient from '../../api/client'
import type { DeadlineItemDto } from '../../api/generated/dtos'
import { TimelineEventDetailsModal, type TimelineEventDetails } from '../timeline/TimelineEventDetailsModal'

export function DeadlineList({ scope = 'mine', from, to, compact = false }: {
  scope?: 'mine' | 'agency'
  from?: string
  to?: string
  compact?: boolean
}) {
  const [selectedDeadline, setSelectedDeadline] = useState<DeadlineItemDto | null>(null)
  const start = from ?? new Date().toISOString().slice(0, 10)
  const end = to ?? new Date(Date.now() + (compact ? 7 : 30) * 86400000).toISOString().slice(0, 10)
  const { data, isLoading, isError } = useQuery<DeadlineItemDto[]>({
    queryKey: ['deadlines', scope, start, end],
    queryFn: async () => (await apiClient.get('/deadlines', { params: { from: `${start}T00:00:00Z`, to: `${end}T00:00:00Z`, scope } })).data,
  })
  if (isLoading) return <p className="p-4 text-sm text-muted">Caricamento scadenze…</p>
  if (isError) return <div className="p-4 text-sm text-negative"><AlertTriangle className="mr-2 inline" size={16} />Impossibile caricare le scadenze.</div>
  if (!data?.length) return <div className="p-4 text-center text-sm text-muted"><CalendarClock className="mx-auto mb-2" size={24} />Nessuna scadenza nel periodo.</div>
  return <><div className="divide-y divide-border-subtle">
    {data.slice(0, compact ? 6 : undefined).map(item => (
      <div key={item.deadlineId} className="block px-4 py-3 hover:bg-surface">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <Link to={item.url} className="m-0 block truncate text-sm font-medium text-link hover:underline">{item.title}</Link>
            <p className="m-0 mt-1 text-xs text-muted">{item.type}{item.assignee ? ` · ${item.assignee}` : ''}</p>
          </div>
          <span className={`shrink-0 text-xs ${item.severity === 'overdue' ? 'text-negative' : item.severity === 'urgent' ? 'text-warning-700' : 'text-muted'}`}>
            <Clock className="mr-1 inline" size={12} />{new Date(item.date).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', timeZone: 'UTC' })}
          </span>
        </div>
        <button type="button" onClick={() => setSelectedDeadline(item)} className="mt-2 rounded px-2 py-0.5 text-xs font-medium text-link hover:bg-primary-50">Dettagli</button>
      </div>
    ))}
  </div><TimelineEventDetailsModal event={selectedDeadline ? ({
    heading: 'Dettaglio scadenza', title: selectedDeadline.title, typeLabel: selectedDeadline.type, entityLabel: 'Scadenza',
    createdAt: selectedDeadline.date, dateLabel: 'Data', assigneeName: selectedDeadline.assignee,
    priorityLabel: selectedDeadline.severity === 'overdue' ? 'Scaduta' : selectedDeadline.severity === 'urgent' ? 'Urgente' : 'In programma',
    detailUrl: selectedDeadline.url, detailUrlLabel: 'Apri elemento',
    propertyId: selectedDeadline.propertyId, propertyAddress: selectedDeadline.propertyLabel,
    customerId: selectedDeadline.customerId, customerName: selectedDeadline.customerName,
  } satisfies TimelineEventDetails) : null} onClose={() => setSelectedDeadline(null)} /></>
}
